import React, { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  makeStyles, 
  TextField, 
  Grid, 
  Box 
} from '@material-ui/core';
import { configApiRef } from '@backstage/core-plugin-api';
import { identityApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  dateRangeContainer: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  content: {
    flex: 1,
    padding: theme.spacing(2, 2, 1, 2),
  },
  chartContainer: {
    position: 'relative',
    height: '300px',
    width: '100%',
  },
}));

interface DataPoint {
  date: string;
  participationCount: number;
  clarityCount: number;
}

interface MetricData {
  id: string;
  name: string;
  tribe: string;
  target_name: string;
  participation: boolean;
  participation_score: number;
  clarity: boolean;
  clarity_score: number;
  created_at: string;
}

interface QualityMetricLineChartProps {
  title?: string;
  color?: string;
  yAxisLabel?: string;
  startDate?: string; // ISO date string (YYYY-MM-DD)
  endDate?: string;   // ISO date string (YYYY-MM-DD)
  isUser?: boolean;
}

export const QualityMetricLineChart = ({
  title = 'Quality Metrics',
  yAxisLabel = 'Score',
  startDate: initialStartDate,
  endDate: initialEndDate,
  isUser,
}: QualityMetricLineChartProps) => {
  const today = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(today.getDate() - 7); // Set to 7 days before today
  
  const [startDate, setStartDate] = useState<Date>(initialStartDate ? new Date(initialStartDate) : defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(initialEndDate ? new Date(initialEndDate) : today);
  const [chartData, setChartData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const { entity } = useEntity();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const { token } = await identityApi.getCredentials();
        
        // Build query parameters
        const params = new URLSearchParams({
          tribe: isUser ? '' : entity.metadata.name,
          name: isUser ? entity.metadata.name : '',
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        });

        const response = await fetch(`${baseUrl}/api/quality-metric/metrics?${params.toString()}`, {
          method: 'GET',
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              }
            : {},
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const metrics: MetricData[] = await response.json();
        
        // Group by date and count participation and clarity metrics
        const dailyCounts = metrics.reduce((acc, metric) => {
          const date = new Date(metric.created_at).toISOString().split('T')[0]; // Get YYYY-MM-DD format
          
          if (!acc[date]) {
            acc[date] = { participationCount: 0, clarityCount: 0 };
          }
          
          acc[date].participationCount += metric.participation_score;
          acc[date].clarityCount += metric.clarity_score;
          
          return acc;
        }, {} as Record<string, { participationCount: number; clarityCount: number }>);
        
        // Convert to array of DataPoint and sort by date
        const transformedData = Object.entries(dailyCounts)
          .map(([date, counts]) => ({
            date: new Date(date).toLocaleDateString(),
            ...counts
          }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        console.log('Transformed metrics data:', transformedData);
        setChartData(transformedData);
      } catch (err) {
        console.error('Error fetching metrics:', err);
        setError('Failed to load quality metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [fetch, startDate, endDate]);
  const classes = useStyles();

  // Create display data with default values if empty
  const displayData = chartData.length === 0 
    ? [{ date: new Date().toLocaleDateString(), participationCount: 0, clarityCount: 0 }]
    : chartData;

  // Render filter controls
  const renderFilterControls = () => (
    <Box className={classes.dateRangeContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate.toISOString().split('T')[0]}
            onChange={(e) => setStartDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant="outlined"
            inputProps={{ max: endDate.toISOString().split('T')[0] }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="End Date"
            type="date"
            value={endDate.toISOString().split('T')[0]}
            onChange={(e) => setEndDate(new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant="outlined"
            inputProps={{ 
              min: startDate.toISOString().split('T')[0],
              max: today.toISOString().split('T')[0]
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  // Calculate average scores
  const calculateAverages = () => {
    if (chartData.length === 0) return { participation: '0', clarity: '0' };
    
    const totals = chartData.reduce(
      (acc, curr) => ({
        participation: acc.participation + (curr.participationCount || 0),
        clarity: acc.clarity + (curr.clarityCount || 0),
      }),
      { participation: 0, clarity: 0 }
    );

    return {
      participation: (totals.participation / chartData.length).toFixed(1),
      clarity: (totals.clarity / chartData.length).toFixed(1),
    };
  };

  const averages = calculateAverages();

  const chartDataConfig: ChartData<'line'> = {
    labels: displayData.map(item => item.date),
    datasets: [
      {
        label: `Participation (Avg: ${averages.participation})`,
        data: displayData.map(item => item.participationCount),
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        borderWidth: 2,
        pointBackgroundColor: '#4CAF50',
        pointBorderColor: '#fff',
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#4CAF50',
        pointHoverBorderColor: '#fff',
        pointHitRadius: 10,
        pointBorderWidth: 2,
        tension: 0.3,
        fill: false,
      },
      {
        label: `Clarity (Avg: ${averages.clarity})`,
        data: displayData.map(item => item.clarityCount),
        borderColor: '#2196F3',
        backgroundColor: 'rgba(33, 150, 243, 0.2)',
        borderWidth: 2,
        pointBackgroundColor: '#2196F3',
        pointBorderColor: '#fff',
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#2196F3',
        pointHoverBorderColor: '#fff',
        pointHitRadius: 10,
        pointBorderWidth: 2,
        tension: 0.3,
        fill: false,
      },
      {
        label: 'Total',
        data: displayData.map(item => item.participationCount + item.clarityCount),
        borderColor: '#9C27B0',
        backgroundColor: 'rgba(156, 39, 176, 0.2)',
        borderWidth: 2,
        borderDash: [5, 5],
        pointBackgroundColor: '#9C27B0',
        pointBorderColor: '#fff',
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#9C27B0',
        pointHoverBorderColor: '#fff',
        pointHitRadius: 10,
        pointBorderWidth: 2,
        tension: 0.3,
        fill: false,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        display: true,
        labels: {
          boxWidth: 15,
          padding: 15,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0
        },
        title: {
          display: true,
          text: yAxisLabel,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardHeader title={title} action={renderFilterControls()} />
        <CardContent className={classes.content}>
          <div>Loading metrics...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={classes.root}>
        <CardHeader title={title} action={renderFilterControls()} />
        <CardContent className={classes.content}>
          <div>Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card className={classes.root}>
        <CardHeader title={title} action={renderFilterControls()} />
        <CardContent className={classes.content}>
          <div>No metrics data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.root}>
      <CardHeader 
        title={title} 
        subheader={`of ${entity.metadata.name}`}
        action={renderFilterControls()} 
      />
      <CardContent className={classes.content}>
        <div className={classes.chartContainer}>
          <Line data={chartDataConfig} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

export default QualityMetricLineChart;
